import React, { useCallback, useEffect, useMemo, useState } from "react";
import CallCenterModal from "./listing/CallCenterModal";
import { Link } from "react-router-dom";
import {
  FiChevronDown,
  FiExternalLink,
  FiEye,
  FiFile,
  FiSearch,
} from "react-icons/fi";
import useCallCenterT from "../../hooks/useCallCenterT";
import { listTicketUploadLogs } from "../../service/CallCenterTickets";
import TableSkeleton from "./listing/TableSkeleton";
import ListingEmptyState from "./listing/ListingEmptyState";
import ListingFilterHeader from "./listing/ListingFilterHeader";
import ListingPaginationFooter from "./listing/ListingPaginationFooter";
import StatusBadge from "./listing/StatusBadge";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_CARD_CLASS,
  LISTING_FILTER_CARD_CLASS,
  LISTING_ICON_ACTION_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
  LISTING_TABLE_HEAD_CLASS,
  LISTING_TABLE_HEADER_CELL_CLASS,
  LISTING_TABLE_ROW_CLASS,
} from "./listing/listingStyles";

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const DETAIL_PAGE_SIZE = 25;

const OUTCOME_FILTERS = ["ALL", "HAS_FAILURES", "HAS_IGNORED", "CLEAN"];

const formatDateTime = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const rowOutcomeStatus = (row) => {
  if (row.outcome === "CREATED" || row.success) {
    return { status: "COMPLETED", labelKey: "ccUploadRowCreated", tab: "CREATED" };
  }
  if (row.outcome === "IGNORED") {
    return { status: "SCHEDULED", labelKey: "ccUploadRowIgnored", tab: "IGNORED" };
  }
  return { status: "CANCELLED", labelKey: "ccUploadRowFailed", tab: "FAILED" };
};

const ProgressBar = ({ completed, total }) => {
  const safeTotal = Math.max(Number(total) || 0, 1);
  const safeCompleted = Math.min(Math.max(Number(completed) || 0, 0), safeTotal);
  const percent = Math.round((safeCompleted / safeTotal) * 100);
  return (
    <div className="min-w-[100px]">
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="font-medium text-slate-700">{percent}%</span>
        <span className="text-slate-400">
          {safeCompleted}/{safeTotal}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

const SummaryStat = ({ label, value, tone = "slate" }) => {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    red: "border-red-200 bg-red-50 text-red-800",
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${tones[tone] || tones.slate}`}>
      <div className="text-[11px] font-medium uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-1 text-xl font-bold">{value.toLocaleString()}</div>
    </div>
  );
};

const DetailTab = ({ active, label, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
      active
        ? "bg-[#2B7FFF] text-white"
        : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"
    }`}
  >
    {label} ({count})
  </button>
);

const UploadLogDetailModal = ({ log, onClose, t }) => {
  const [activeTab, setActiveTab] = useState("ALL");
  const [detailSearch, setDetailSearch] = useState("");
  const [detailPage, setDetailPage] = useState(0);

  const allRows = useMemo(() => log?.results || [], [log]);

  const tabCounts = useMemo(
    () => ({
      ALL: allRows.length,
      CREATED: allRows.filter((row) => row.outcome === "CREATED" || row.success).length,
      IGNORED: allRows.filter((row) => row.outcome === "IGNORED").length,
      FAILED: allRows.filter((row) => row.outcome === "FAILED" || (!row.success && row.outcome !== "IGNORED")).length,
    }),
    [allRows]
  );

  const filteredRows = useMemo(() => {
    let rows = allRows;
    if (activeTab === "CREATED") {
      rows = rows.filter((row) => row.outcome === "CREATED" || row.success);
    } else if (activeTab === "IGNORED") {
      rows = rows.filter((row) => row.outcome === "IGNORED");
    } else if (activeTab === "FAILED") {
      rows = rows.filter((row) => row.outcome === "FAILED" || (!row.success && row.outcome !== "IGNORED"));
    }
    const query = detailSearch.trim().toLowerCase();
    if (query) {
      rows = rows.filter((row) => String(row.accountNo || "").toLowerCase().includes(query));
    }
    return rows;
  }, [allRows, activeTab, detailSearch]);

  const detailTotalPages = Math.max(Math.ceil(filteredRows.length / DETAIL_PAGE_SIZE), filteredRows.length > 0 ? 1 : 0);
  const pagedRows = filteredRows.slice(detailPage * DETAIL_PAGE_SIZE, (detailPage + 1) * DETAIL_PAGE_SIZE);

  useEffect(() => {
    setDetailPage(0);
  }, [activeTab, detailSearch]);

  if (!log) return null;

  return (
    <CallCenterModal
      open={Boolean(log)}
      onClose={onClose}
      eyebrow={t("ccUploadHistoryDetailTitle")}
      title={log.fileName}
      subtitle={formatDateTime(log.createdAt)}
      maxWidth="4xl"
      maxHeight="90vh"
      closeLabel={t("ccClose")}
      contentVariant="none"
      bodyClassName="min-h-0 flex-1 flex flex-col overflow-hidden p-0"
    >
          <div className="grid shrink-0 grid-cols-2 gap-2 border-b border-slate-200 bg-white px-4 py-4 sm:grid-cols-4 sm:px-6">
            <SummaryStat label={t("ccUploadSuccessCount")} value={log.successCount ?? 0} tone="emerald" />
            <SummaryStat label={t("ccUploadIgnoredCount")} value={log.ignoredCount ?? 0} tone="amber" />
            <SummaryStat label={t("ccUploadFailedCount")} value={log.failureCount ?? 0} tone="red" />
            <SummaryStat label={t("ccUploadTotalRows")} value={log.totalRows ?? 0} tone="slate" />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6">
            <div className="mb-4 flex flex-wrap gap-2">
              {["ALL", "CREATED", "IGNORED", "FAILED"].map((tab) => (
                <DetailTab
                  key={tab}
                  active={activeTab === tab}
                  label={t(`ccUploadHistoryTab_${tab}`)}
                  count={tabCounts[tab]}
                  onClick={() => setActiveTab(tab)}
                />
              ))}
            </div>

            <div className="mb-4">
              <label className={FILTER_LABEL_CLASS}>{t("ccAccountPlanAccount")}</label>
              <div className="relative max-w-sm">
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={detailSearch}
                  onChange={(event) => setDetailSearch(event.target.value)}
                  placeholder={t("ccSearchAccountPlaceholder")}
                  className={`${FILTER_FIELD_CLASS} pl-9`}
                />
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className={LISTING_TABLE_HEAD_CLASS}>
                  <tr>
                    <th className={LISTING_TABLE_HEADER_CELL_CLASS}>#</th>
                    <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccAccountPlanAccount")}</th>
                    <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccStatus")}</th>
                    <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccRemarks")}</th>
                    <th className={`${LISTING_TABLE_HEADER_CELL_CLASS} text-right`}>{t("ccActions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-500">
                        {t("ccUploadHistoryNoRowDetails")}
                      </td>
                    </tr>
                  ) : (
                    pagedRows.map((row) => {
                      const outcome = rowOutcomeStatus(row);
                      return (
                        <tr key={`${log.id}-${row.rowNumber}-${row.accountNo || "row"}`} className={LISTING_TABLE_ROW_CLASS}>
                          <td className="px-4 py-3">{row.rowNumber}</td>
                          <td className="px-4 py-3 font-medium text-slate-800">{row.accountNo || t("ccEmDash")}</td>
                          <td className="px-4 py-3">
                            <StatusBadge status={outcome.status} label={t(outcome.labelKey)} />
                          </td>
                          <td className="max-w-md px-4 py-3 text-slate-600">
                            <div className="truncate" title={row.message || ""}>
                              {row.message || t("ccEmDash")}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {row.ticketId ? (
                              <Link
                                to={`/call-center/tickets?ticketId=${row.ticketId}${
                                  row.accountNo ? `&accountNo=${encodeURIComponent(row.accountNo)}` : ""
                                }`}
                                className={LISTING_ICON_ACTION_CLASS}
                                title={t("ccAccountPlanOpenTicket")}
                              >
                                <FiExternalLink className="h-4 w-4" />
                              </Link>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {filteredRows.length > DETAIL_PAGE_SIZE && (
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  disabled={detailPage <= 0}
                  onClick={() => setDetailPage((value) => Math.max(0, value - 1))}
                  className="h-9 rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 disabled:opacity-50"
                >
                  {t("ccPrevious")}
                </button>
                <span className="min-w-[72px] text-center text-sm text-slate-600">
                  {detailPage + 1} / {detailTotalPages}
                </span>
                <button
                  type="button"
                  disabled={detailPage + 1 >= detailTotalPages}
                  onClick={() => setDetailPage((value) => value + 1)}
                  className="h-9 rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 disabled:opacity-50"
                >
                  {t("ccNext")}
                </button>
              </div>
            )}
          </div>
    </CallCenterModal>
  );
};

const TicketUploadHistoryPanel = ({
  compact = false,
  compactLimit = 5,
  highlightLogId = null,
  showViewAllLink = false,
  refreshKey = 0,
  openLatestAfterRefresh = false,
  onLatestOpened = null,
}) => {
  const t = useCallCenterT();
  const [uploadLogs, setUploadLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(compact ? compactLimit : 10);
  const [fileSearch, setFileSearch] = useState("");
  const [fileSearchApplied, setFileSearchApplied] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState("ALL");
  const [detailLog, setDetailLog] = useState(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const logs = await listTicketUploadLogs();
      const normalized = Array.isArray(logs) ? logs : [];
      setUploadLogs(normalized);
      return normalized;
    } catch (err) {
      setError(err?.message || t("ccUploadHistoryLoadFailed"));
      setUploadLogs([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    const hasInProgress = uploadLogs.some(
      (log) => log.processingStatus === "PENDING" || log.processingStatus === "PROCESSING"
    );
    if (!hasInProgress) return undefined;

    const timer = setInterval(() => {
      loadLogs().catch(() => undefined);
    }, 5000);

    return () => clearInterval(timer);
  }, [uploadLogs, loadLogs]);

  useEffect(() => {
    if (refreshKey <= 0) return;
    let cancelled = false;
    loadLogs().then((logs) => {
      if (cancelled || !openLatestAfterRefresh || !logs[0]) return;
      setDetailLog(logs[0]);
      onLatestOpened?.();
    });
    return () => {
      cancelled = true;
    };
  }, [refreshKey, openLatestAfterRefresh, loadLogs, onLatestOpened]);

  useEffect(() => {
    if (!highlightLogId || uploadLogs.length === 0) return;
    const match = uploadLogs.find((log) => log.id === highlightLogId);
    if (match) setDetailLog(match);
  }, [highlightLogId, uploadLogs]);

  const aggregated = useMemo(
    () =>
      uploadLogs.reduce(
        (acc, log) => ({
          uploads: acc.uploads + 1,
          created: acc.created + (log.successCount ?? 0),
          ignored: acc.ignored + (log.ignoredCount ?? 0),
          failed: acc.failed + (log.failureCount ?? 0),
          rows: acc.rows + (log.totalRows ?? 0),
        }),
        { uploads: 0, created: 0, ignored: 0, failed: 0, rows: 0 }
      ),
    [uploadLogs]
  );

  const filteredLogs = useMemo(() => {
    let rows = uploadLogs;
    const query = fileSearchApplied.trim().toLowerCase();
    if (query) {
      rows = rows.filter((log) => String(log.fileName || "").toLowerCase().includes(query));
    }
    if (outcomeFilter === "HAS_FAILURES") {
      rows = rows.filter((log) => (log.failureCount ?? 0) > 0);
    } else if (outcomeFilter === "HAS_IGNORED") {
      rows = rows.filter((log) => (log.ignoredCount ?? 0) > 0);
    } else if (outcomeFilter === "CLEAN") {
      rows = rows.filter((log) => (log.failureCount ?? 0) === 0 && (log.ignoredCount ?? 0) === 0);
    }
    return rows;
  }, [uploadLogs, fileSearchApplied, outcomeFilter]);

  const totalPages = Math.max(Math.ceil(filteredLogs.length / pageSize), filteredLogs.length > 0 ? 1 : 0);
  const pagedLogs = filteredLogs.slice(page * pageSize, (page + 1) * pageSize);

  const hasActiveFilters = Boolean(fileSearchApplied || outcomeFilter !== "ALL");

  const applyFilters = () => {
    setPage(0);
    setFileSearchApplied(fileSearch.trim());
  };

  const resetFilters = () => {
    setFileSearch("");
    setFileSearchApplied("");
    setOutcomeFilter("ALL");
    setPage(0);
  };

  const openDetail = (log) => setDetailLog(log);
  const closeDetail = () => setDetailLog(null);

  return (
    <>
      {!compact && (
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
          <SummaryStat label={t("ccUploadHistoryStatUploads")} value={aggregated.uploads} />
          <SummaryStat label={t("ccUploadTotalRows")} value={aggregated.rows} />
          <SummaryStat label={t("ccUploadSuccessCount")} value={aggregated.created} tone="emerald" />
          <SummaryStat label={t("ccUploadIgnoredCount")} value={aggregated.ignored} tone="amber" />
          <SummaryStat label={t("ccUploadFailedCount")} value={aggregated.failed} tone="red" />
        </div>
      )}

      {error && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{error}</span>
          <button type="button" onClick={loadLogs} className="font-semibold hover:underline">
            {t("ccRefresh")}
          </button>
        </div>
      )}

      <section className={LISTING_CARD_CLASS}>
        <div className={compact ? "p-4 pb-0" : "p-5 pb-0"}>
          <ListingFilterHeader
            title={compact ? t("ccUploadHistoryRecentTitle") : t("ccUploadHistoryTitle")}
            page={page}
            totalPages={totalPages}
            totalElements={filteredLogs.length}
            resultsLabel={t("ccUploadHistoryResultsLabel")}
            showingPageLabel={t("ccShowingPage")}
            ofLabel={t("ccOf")}
            refreshLabel={t("ccRefresh")}
            loading={loading}
            onRefresh={loadLogs}
            actions={
              showViewAllLink && compact ? (
                <Link
                  to="/call-center/ticket-upload-history"
                  className="text-sm font-semibold text-[#2B7FFF] hover:text-[#1d4ed8]"
                >
                  {t("ccUploadHistoryViewAll")}
                </Link>
              ) : null
            }
          />

          {!compact && (
            <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-12">
              <div className="xl:col-span-5">
                <label className={FILTER_LABEL_CLASS}>{t("ccUploadHistoryFile")}</label>
                <div className="flex gap-2">
                  <input
                    value={fileSearch}
                    onChange={(event) => setFileSearch(event.target.value)}
                    onKeyDown={(event) => event.key === "Enter" && applyFilters()}
                    placeholder={t("ccUploadHistorySearchPlaceholder")}
                    className={FILTER_FIELD_CLASS}
                  />
                  <button
                    type="button"
                    onClick={applyFilters}
                    className={`${LISTING_PRIMARY_BUTTON_CLASS} shrink-0 px-3`}
                  >
                    <FiSearch className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="xl:col-span-4">
                <label className={FILTER_LABEL_CLASS}>{t("ccUploadHistoryOutcomeFilter")}</label>
                <select
                  value={outcomeFilter}
                  onChange={(event) => {
                    setPage(0);
                    setOutcomeFilter(event.target.value);
                  }}
                  className={FILTER_FIELD_CLASS}
                >
                  {OUTCOME_FILTERS.map((filter) => (
                    <option key={filter} value={filter}>
                      {t(`ccUploadHistoryOutcome_${filter}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end xl:col-span-3">
                <button
                  type="button"
                  onClick={resetFilters}
                  disabled={!hasActiveFilters}
                  className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t("ccClearFilters")}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className={`overflow-x-auto ${compact ? "px-4 pb-2" : "px-5 pb-2"}`}>
          <table className="min-w-full text-sm">
            <thead className={LISTING_TABLE_HEAD_CLASS}>
              <tr>
                <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccUploadHistoryFile")}</th>
                <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccUploadHistoryDate")}</th>
                {!compact && (
                  <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccUploadHistorySuccessRate")}</th>
                )}
                <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccUploadSuccessCount")}</th>
                <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccUploadIgnoredCount")}</th>
                <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccUploadFailedCount")}</th>
                <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccUploadTotalRows")}</th>
                <th className={`${LISTING_TABLE_HEADER_CELL_CLASS} text-right`}>{t("ccActions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton rows={compact ? 3 : 5} columns={compact ? 7 : 8} />
              ) : pagedLogs.length === 0 ? (
                <tr>
                  <td colSpan={compact ? 7 : 8}>
                    <ListingEmptyState
                      icon={FiFile}
                      title={t("ccUploadHistoryEmptyTitle")}
                      hint={
                        hasActiveFilters ? t("ccUploadHistoryEmptyFiltered") : t("ccUploadHistoryEmpty")
                      }
                    />
                  </td>
                </tr>
              ) : (
                pagedLogs.map((log) => (
                  <tr key={log.id} className={LISTING_TABLE_ROW_CLASS}>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2 font-semibold text-slate-900">
                        <FiFile className="h-4 w-4 shrink-0 text-slate-400" />
                        <span className="break-all">{log.fileName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-slate-600">{formatDateTime(log.createdAt)}</td>
                    {!compact && (
                      <td className="px-4 py-4">
                        <ProgressBar completed={log.successCount ?? 0} total={log.totalRows ?? 0} />
                      </td>
                    )}
                    <td className="px-4 py-4">
                      <StatusBadge status="COMPLETED" label={String(log.successCount ?? 0)} />
                    </td>
                    <td className="px-4 py-4">
                      <StatusBadge status="SCHEDULED" label={String(log.ignoredCount ?? 0)} />
                    </td>
                    <td className="px-4 py-4">
                      <StatusBadge status="CANCELLED" label={String(log.failureCount ?? 0)} />
                    </td>
                    <td className="px-4 py-4 font-medium text-slate-700">{log.totalRows ?? 0}</td>
                    <td className="px-4 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => openDetail(log)}
                        title={t("ccUploadViewDetails")}
                        className={LISTING_ICON_ACTION_CLASS}
                      >
                        <FiEye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!compact && (
          <ListingPaginationFooter
            totalElements={filteredLogs.length}
            resultsLabel={t("ccUploadHistoryResultsLabel")}
            page={page}
            totalPages={totalPages}
            loading={loading}
            onPrevious={() => setPage((value) => Math.max(0, value - 1))}
            onNext={() => setPage((value) => value + 1)}
            previousLabel={t("ccPrevious")}
            nextLabel={t("ccNext")}
            extra={
              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value) || 10);
                  setPage(0);
                }}
                className="h-9 rounded-xl border border-slate-200 bg-white px-2 text-xs text-slate-600"
                aria-label={t("ccRows")}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            }
          />
        )}

        {compact && filteredLogs.length > compactLimit && (
          <div className="border-t border-slate-100 px-4 py-3 text-center">
            <Link
              to="/call-center/ticket-upload-history"
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#2B7FFF] hover:text-[#1d4ed8]"
            >
              {t("ccUploadHistoryViewAll")}
              <FiChevronDown className="h-4 w-4 rotate-[-90deg]" />
            </Link>
          </div>
        )}
      </section>

      <UploadLogDetailModal log={detailLog} onClose={closeDetail} t={t} />
    </>
  );
};

export default TicketUploadHistoryPanel;
